import sortFilters, {getJoinedFilterPath} from "../sortFilters"
import {getFiltersWithoutPriceAndSort} from "../getFiltersFromMerchandisedPath"
import {getAreaSegment} from "../getAreaSegment"
import alphabeticaliseFiltersUrlSegment, {
    alphabeticaliseCategoryUrlSegment,
} from "../seo/alphabeticaliseCategoryFilters"
import {indexFilters, indexMerchandiseURLFilters} from "../seo/indexFilters"
import {CategoryUrlSegments} from "../seo/getCategorySearchUrlSegment"
import {SearchApiResponse} from "../../models/searchApi"
import {Filter} from "../../models/Filter"
import {isFilterFacet} from "../../models/Filter/typeGuards"
import {SegmentsAndFilters, getSegmentsAndFiltersFromMerchandisePath} from "../getSeoTitleForMerchandiseUrl"
import {State} from "../../models/State"
import {AreaSegmentOptions} from "../getAreaSegment/constants"
import {Facet} from "../../models/Facet"
import {CATEGORY} from "../../config/constants"

export const buildFacetUrlsForAFilter = (
    filter: Filter,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    languagePrefix: string,
) => {
    if (!isFilterFacet(filter)) return ""

    if (filter.name === "gender")
        return filter.facets?.length > 0
            ? `<ul>${filter.facets.reduce(
                  (stringValue, facet) =>
                      stringValue + buildUrlForAGenderFacet(facet, originalUrl, urlSegments, languagePrefix),
                  "",
              )}</ul>`
            : ""

    return filter.facets?.length > 0
        ? `<ul>${filter.facets.reduce(
              (stringValue, facet) => stringValue + buildUrlForAFacet(facet, originalUrl, urlSegments, languagePrefix),
              "",
          )}</ul>`
        : ""
}

export const buildMerchanisedFacetUrlsForAFilter = (
    filter: Filter,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    languagePrefix: string,
    merchandisedURLFilterDepthCount: number,
    segmentsAndFilter: SegmentsAndFilters,
    structuredBrowsingEnabled: boolean,
    areaSegment: (typeof AreaSegmentOptions)[keyof typeof AreaSegmentOptions] | false,
    facets: Facet[],
) => {
    if (!isFilterFacet(filter)) return ""

    if (
        filter.name === "gender" &&
        filter.facets?.length > 0 &&
        ((areaSegment === "shop" && segmentsAndFilter.segments.length === 0) || areaSegment !== "shop")
    ) {
        return `<ul>${filter.facets.reduce(
            (stringValue, facet) =>
                stringValue +
                buildMerchanisedUrlForAGenderFacet(
                    facet,
                    originalUrl,
                    urlSegments,
                    languagePrefix,
                    merchandisedURLFilterDepthCount,
                ),
            "",
        )}</ul>`
    }

    const categoryFacets = facets.filter(facet => facet.v.startsWith(`${CATEGORY}:`))
    if (filter.name === "category" && structuredBrowsingEnabled && filter.facets.length > 0) {
        return `<ul>${categoryFacets.reduce(
            (stringValue, facet) =>
                stringValue + buildMerchanisedUrlForAStructuredBrowsingFacet(facet.n, facet.u, languagePrefix),
            "",
        )}</ul>`
    }
    return (filter.name !== "gender" && filter.name !== "category" && filter.facets?.length > 0) ||
        (filter.name === "category" && !structuredBrowsingEnabled && filter.facets?.length > 0)
        ? `<ul>${filter.facets.reduce(
              (stringValue, facet) =>
                  stringValue +
                  buildMerchanisedUrlForAFacet(
                      facet,
                      originalUrl,
                      urlSegments,
                      languagePrefix,
                      merchandisedURLFilterDepthCount,
                  ),
              "",
          )}</ul>`
        : ""
}

export const buildFilterNameAndHyphened = (facetValue: string) => {
    let filterName = ""
    let vHyphened = ""

    /**
     * In one loop we extract the filter name and create the hyphenated version of the facet value
     */
    for (let index = 0; index < facetValue.length; index += 1) {
        const element = facetValue[index]

        // if filterName is not empty, we have already reached the colon and are now adding the next char to the value
        if (filterName && index < facetValue.length - 1) {
            filterName += facetValue[index + 1]
        }

        // if the current char is the colon it will be replaced with a hyphen
        // and if filterName has not yet been populated, the next char will be used to do so
        if (element === ":") {
            vHyphened += "-"
            if (filterName === "") {
                filterName += facetValue[index + 1]
            }
        } else if (element === " ") {
            vHyphened += ""
        } else {
            vHyphened += element
        }
    }

    return {filterName, vHyphened}
}

// Facet value = "colour:red"
// Original URL = "https://next.co.uk/en/shop/gender-women/colour-red" etc
// Url Segments = "gender-women", "colour-red"
export const buildUrlForAFacet = (
    facetValue: string,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    languagePrefix: string,
) => {
    const {filterName, vHyphened} = buildFilterNameAndHyphened(facetValue)
    const areaSegment = getAreaSegment(originalUrl)

    const alphabeticalisedFilters = alphabeticaliseFiltersUrlSegment(`${urlSegments.filter}-${vHyphened}`)

    const noIndex =
        originalUrl.includes(vHyphened) || !indexFilters(alphabeticalisedFilters.sortedFilters) ? `/noindex` : ``

    const capitalisedFilterName = filterName.charAt(0).toUpperCase() + filterName.slice(1)

    return `<li><a href="${languagePrefix}/${areaSegment ? `${areaSegment}/` : ``}${urlSegments.category.replace(
        /-0/g,
        "",
    )}/${alphabeticalisedFilters.sortedFilters}${noIndex}" >${capitalisedFilterName}</a></li>`
}

// Facet value = "colour:red"
// Original URL = "https://next.co.uk/en/shop/gender-women/colour-red" etc
// Url Segments = "gender-women", "colour-red"
export const buildMerchanisedUrlForAFacet = (
    facetValue: string,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    languagePrefix: string,
    merchandisedURLFilterDepthCount: number,
) => {
    const {filterName, vHyphened} = buildFilterNameAndHyphened(facetValue)
    const areaSegment = getAreaSegment(originalUrl)
    const filters = urlSegments.filter !== "" ? `${urlSegments.filter}-${vHyphened}` : vHyphened
    const filtersArray = getFiltersWithoutPriceAndSort(filters)

    const newFilters = getJoinedFilterPath(sortFilters(filtersArray))
    const noIndex = !indexMerchandiseURLFilters(filters, merchandisedURLFilterDepthCount) ? `/noindex` : ``

    const capitalisedFilterName = filterName.charAt(0).toUpperCase() + filterName.slice(1)

    return `<li><a href="${languagePrefix}/${areaSegment ? `${areaSegment}/` : ``}${
        urlSegments.category ? `${urlSegments.category}/` : ""
    }f/${newFilters}${noIndex}" >${capitalisedFilterName}</a></li>`
}

export const buildMerchanisedUrlForAStructuredBrowsingFacet = (
    facetName: string,
    facetURL: string | undefined,
    languagePrefix: string,
) => {
    if (!facetURL) return ""
    const capitalisedFilterName = facetName.charAt(0).toUpperCase() + facetName.slice(1)
    return `<li><a href="${languagePrefix}${facetURL}">${capitalisedFilterName}</a></li>`
}

// Gender facets get added to the category URL segment
// Kids genders are allowed to be duplicated without adding /noindex
// Adult genders are not
export const buildMerchanisedUrlForAGenderFacet = (
    facetValue: string,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    languagePrefix: string,
    merchandisedURLFilterDepthCount: number,
) => {
    const {filterName, vHyphened} = buildFilterNameAndHyphened(facetValue)
    const areaSegment = getAreaSegment(originalUrl)

    if (originalUrl.includes(vHyphened)) return ""

    const filters = urlSegments.filter !== "" ? `${urlSegments.filter}-${vHyphened}` : vHyphened

    const filtersArray = getFiltersWithoutPriceAndSort(filters)
    const newFilters = getJoinedFilterPath(sortFilters(filtersArray))

    let noIndex = !indexMerchandiseURLFilters(newFilters, merchandisedURLFilterDepthCount) ? `noindex` : ``
    if (noIndex) {
        noIndex = urlSegments.filter ? `/${noIndex}` : noIndex
    }

    const capitalisedFilterName = filterName[0].toUpperCase() + filterName.slice(1)

    const href = `${languagePrefix}/${areaSegment ? `${areaSegment}/` : ``}${
        urlSegments.category ? `${urlSegments.category}/` : ""
    }f/${newFilters}${noIndex}`

    return `<li><a href="${href}" >${capitalisedFilterName}</a></li>`
}

// Gender facets get added to the category URL segment
// Kids genders are allowed to be duplicated without adding /noindex
// Adult genders are not
export const buildUrlForAGenderFacet = (
    facetValue: string,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    languagePrefix: string,
) => {
    const {filterName, vHyphened} = buildFilterNameAndHyphened(facetValue)
    const areaSegment = getAreaSegment(originalUrl)

    if (originalUrl.includes(vHyphened)) return ""

    const alphabeticalisedCategorySegment = alphabeticaliseCategoryUrlSegment(
        `${urlSegments.category.replace(/-0/g, "")}-${vHyphened}`,
    )

    let noIndex = !indexFilters(alphabeticalisedCategorySegment) ? `noindex` : ``
    if (noIndex) {
        noIndex = urlSegments.filter ? `/${noIndex}` : noIndex
    }

    const capitalisedFilterName = filterName[0].toUpperCase() + filterName.slice(1)

    let href = `${languagePrefix}/${areaSegment ? `${areaSegment}/` : ``}${alphabeticalisedCategorySegment}/${
        urlSegments.filter
    }${noIndex}`

    href = urlSegments.filter.length === 0 && noIndex.length === 0 ? `${href.slice(0, href.length - 1)}-0` : href

    return `<li><a href="${href}" >${capitalisedFilterName}</a></li>`
}

// Data Search API Response with new filter options
// Original URL = "https://next.co.uk/en/shop/gender-women/colour-red" etc
// Url Segments = "gender-women", "colour-red"
// Build a url for every filter / Facet from the API response, apending to the end of the original url
export const getSEOFiltersHTML = (
    data: SearchApiResponse,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    siteUrl: string,
) => {
    const languagePrefix = getLanguagePrefix(siteUrl)
    const areaSegment = getAreaSegment(originalUrl)
    let navHTML = "<nav>"
    if (data.items.length > 0 && originalUrl.includes(`/${areaSegment}`)) {
        Object.values(data.filters).forEach(value => {
            navHTML += buildFacetUrlsForAFilter(value, originalUrl, urlSegments, languagePrefix)
        })

        navHTML += `</nav>`
    }

    const KilobytesFromNFR = 200 * 1024
    const size = Math.floor(navHTML.length / 1024)
    if (navHTML.length >= KilobytesFromNFR) {
        navHTML = ""
    }
    const HTMLResponse = `<noscript id="embeddedFiltersContent">${navHTML}<p>filters:${
        Object.keys(data.facets).length
    } size:${size} KB</p></noscript>`
    return HTMLResponse
}

export const getMerchandisedSEOFiltersHTML = (
    data: SearchApiResponse,
    originalUrl: string,
    urlSegments: CategoryUrlSegments,
    siteUrl: string,
    merchandisedURLFilterDepthCount: number,
    state: State,
) => {
    const segmentsAndFilters = getSegmentsAndFiltersFromMerchandisePath(
        originalUrl.replace(siteUrl, "").split("?")[0],
        state,
    )
    segmentsAndFilters.segments = segmentsAndFilters.segments.filter(segment => segment !== "")
    const languagePrefix = getLanguagePrefix(siteUrl)
    const areaSegment = getAreaSegment(originalUrl)
    let navHTML = "<nav>"
    if (data.items.length > 0 && originalUrl.includes(`/${areaSegment}`)) {
        Object.values(data.filters).forEach(value => {
            navHTML += buildMerchanisedFacetUrlsForAFilter(
                value,
                originalUrl,
                urlSegments,
                languagePrefix,
                merchandisedURLFilterDepthCount,
                segmentsAndFilters,
                areaSegment === "shop" && state.search.enableStructuredBrowsing,
                areaSegment,
                Object.values(data.facets),
            )
        })

        navHTML += `</nav>`
    }

    const KilobytesFromNFR = 200 * 1024
    const size = Math.floor(navHTML.length / 1024)
    if (navHTML.length >= KilobytesFromNFR) {
        navHTML = ""
    }
    const HTMLResponse = `<noscript id="embeddedFiltersContent">${navHTML}<p>filters:${
        Object.keys(data.facets).length
    } size:${size} KB</p></noscript>`
    return HTMLResponse
}

export const getLanguagePrefix = siteUrl => {
    const languagePrefix = new URL(siteUrl).pathname

    if (languagePrefix.length === 1) return ""

    return languagePrefix
}
